/**
 * Toast Notification Service
 * Simple toast notification system without external dependencies
 */

export type ToastType = 'success' | 'error' | 'info' | 'warning';

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  showAsEchobot?: boolean;
}

interface EchobotNotification {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
}

type ToastCallback = (toasts: Toast[]) => void;
type EchobotCallback = (notifications: EchobotNotification[]) => void;

class ToastService {
  private toasts: Toast[] = [];
  private listeners: Set<ToastCallback> = new Set();
  private echobotListeners: Set<EchobotCallback> = new Set();
  private echobotNotifications: EchobotNotification[] = [];
  private idCounter = 0;

  /**
   * Subscribe to toast updates
   */
  subscribe(callback: ToastCallback): () => void {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  /**
   * Notify all listeners of toast changes
   */
  private notify() {
    this.listeners.forEach(callback => callback([...this.toasts]));
  }

  /**
   * Show a success toast
   */
  success(message: string, duration: number = 3000, options?: { showAsEchobot?: boolean }): string {
    if (options?.showAsEchobot) {
      return this.showEchobot('success', message, duration);
    }
    return this.show('success', message, duration);
  }

  /**
   * Show an error toast
   */
  error(message: string, duration: number = 5000, options?: { showAsEchobot?: boolean }): string {
    if (options?.showAsEchobot) {
      return this.showEchobot('error', message, duration);
    }
    return this.show('error', message, duration);
  }

  /**
   * Show an info toast
   */
  info(message: string, duration: number = 3000, options?: { showAsEchobot?: boolean }): string {
    if (options?.showAsEchobot) {
      return this.showEchobot('info', message, duration);
    }
    return this.show('info', message, duration);
  }

  /**
   * Show a warning toast
   */
  warning(message: string, duration: number = 4000, options?: { showAsEchobot?: boolean }): string {
    if (options?.showAsEchobot) {
      return this.showEchobot('warning', message, duration);
    }
    return this.show('warning', message, duration);
  }

  /**
   * Show a toast notification
   */
  private show(type: ToastType, message: string, duration: number): string {
    const id = `toast-${++this.idCounter}-${Date.now()}`;
    const toast: Toast = { id, type, message, duration };

    this.toasts.push(toast);
    this.notify();

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.remove(id);
      }, duration);
    }

    return id;
  }

  /**
   * Remove a specific toast
   */
  remove(id: string): void {
    this.toasts = this.toasts.filter(t => t.id !== id);
    this.notify();
  }

  /**
   * Clear all toasts
   */
  clear(): void {
    this.toasts = [];
    this.notify();
  }

  /**
   * Get current toasts
   */
  getToasts(): Toast[] {
    return [...this.toasts];
  }

  /**
   * Subscribe to Echobot notifications
   */
  subscribeEchobot(callback: EchobotCallback): () => void {
    this.echobotListeners.add(callback);
    return () => this.echobotListeners.delete(callback);
  }

  /**
   * Notify Echobot listeners
   */
  private notifyEchobot() {
    this.echobotListeners.forEach(callback => callback([...this.echobotNotifications]));
  }

  /**
   * Show Echobot notification
   */
  private showEchobot(type: ToastType, message: string, duration: number): string {
    const id = `echobot-${++this.idCounter}-${Date.now()}`;
    const notification: EchobotNotification = { id, type, message, duration };

    this.echobotNotifications.push(notification);
    this.notifyEchobot();

    // Auto-remove after duration
    if (duration > 0) {
      setTimeout(() => {
        this.removeEchobot(id);
      }, duration);
    }

    return id;
  }

  /**
   * Remove specific Echobot notification
   */
  removeEchobot(id: string): void {
    this.echobotNotifications = this.echobotNotifications.filter(n => n.id !== id);
    this.notifyEchobot();
  }

  /**
   * Get current Echobot notifications
   */
  getEchobotNotifications(): EchobotNotification[] {
    return [...this.echobotNotifications];
  }
}

// Export singleton instance
export const toastService = new ToastService();

// Export types
export type { Toast };

